import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../axios';

const initialState = {
  WayPoints: { loading: false, wayPointsList: [], total: 0 },
  SelectedWayPoint: { loading: false, wayPoint: null },
  AllWayPointsFull: { loading: false, wayPointsList: [] }, 
};

export const getAllWayPoints = createAsyncThunk(
  'WayPoint/getAllWayPoints',
  async (args, { rejectWithValue }) => {
    const { page = 0, size = 10, sortBy = 'locationName', sortDirection = 'ASC', search = '' } = args || {};
    try {
      const params = new URLSearchParams({ page, size, sortBy, sortDirection });
      if (search) params.append('search', search);
      const response = await axiosInstance.get(`/way-point?${params}`);
      return {
        waypoints: response.data.content,
        total: response.data.totalElements,
      };
    } catch (error) {
      const customError = {
        name: 'Custom axios error',
        message: error.response?.data?.message || error.message,
      };
      return rejectWithValue(customError);
    }
  }
);

export const getAllWayPointsFull = createAsyncThunk(
  'WayPoint/getAllWayPointsFull',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/way-point/all'); 
      return response.data; 
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch all waypoints');
    }
  }
);

export const addNewWayPoint = createAsyncThunk('WayPoint/addNewWayPoint', async (data) => {
  try {
    const response = await axiosInstance.post(`way-point`, data.wayPoint);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message);
  }
});


export const updateWayPoint = createAsyncThunk('WayPoint/updateWayPoint', async (data) => {
  try {
    const response = await axiosInstance.put(`way-point/${data.wayPointId}`, data.wayPoint);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message);
  }
});

export const deleteWayPoint = createAsyncThunk('WayPoint/deleteWayPoint', async (data) => {
  try {
    await axiosInstance.delete(`way-point/${data.id}`);
    return data.id;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message);
  }
});

export const getWayPointById = createAsyncThunk('WayPoint/getWayPointById', async (data) => {
  try {
    const response = await axiosInstance.get(`way-point/${data.wayPointId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message);
  }
});

export const bulkUploadWayPoints = createAsyncThunk(
  'WayPoint/bulkUploadWayPoints',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('csvFile', file);
      const response = await axiosInstance.post(
        `way-point/upload/csv?validateOnly=false`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const batchDeleteWayPoints = createAsyncThunk(
  'WayPoint/batchDeleteWayPoints',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`way-point-batch-delete`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const WayPointSlice = createSlice({
  name: 'WayPoint',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllWayPoints.pending, (state) => { state.WayPoints.loading = true; })
      .addCase(getAllWayPoints.fulfilled, (state, action) => {
        state.WayPoints.loading = false;
        state.WayPoints.wayPointsList = action.payload.waypoints;
        state.WayPoints.total = action.payload.total;
      })
      .addCase(getAllWayPoints.rejected, (state) => { state.WayPoints.loading = false; })

    
      .addCase(getAllWayPointsFull.pending, (state) => { state.AllWayPointsFull.loading = true; })
      .addCase(getAllWayPointsFull.fulfilled, (state, action) => {
        state.AllWayPointsFull.loading = false;
        state.AllWayPointsFull.wayPointsList = action.payload;
      })
      .addCase(getAllWayPointsFull.rejected, (state) => { state.AllWayPointsFull.loading = false; })

   
      .addCase(addNewWayPoint.pending, (state) => { state.WayPoints.loading = true; })
      .addCase(addNewWayPoint.fulfilled, (state, action) => {
        state.WayPoints.loading = false;
        state.WayPoints.wayPointsList.push(action.payload);
      })
      .addCase(addNewWayPoint.rejected, (state) => { state.WayPoints.loading = false; })

      .addCase(updateWayPoint.pending, (state) => { state.WayPoints.loading = true; })
      .addCase(updateWayPoint.fulfilled, (state, action) => {
        state.WayPoints.loading = false;
        state.WayPoints.wayPointsList = state.WayPoints.wayPointsList.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
      })
      .addCase(updateWayPoint.rejected, (state) => { state.WayPoints.loading = false; })

      .addCase(deleteWayPoint.pending, (state) => { state.WayPoints.loading = true; })
      .addCase(deleteWayPoint.fulfilled, (state, action) => {
        state.WayPoints.loading = false;
        state.WayPoints.wayPointsList = state.WayPoints.wayPointsList.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteWayPoint.rejected, (state) => { state.WayPoints.loading = false; })

      .addCase(getWayPointById.pending, (state) => { state.SelectedWayPoint.loading = true; })
      .addCase(getWayPointById.fulfilled, (state, action) => {
        state.SelectedWayPoint.loading = false;
        state.SelectedWayPoint.wayPoint = action.payload;
      })
      .addCase(getWayPointById.rejected, (state) => { state.SelectedWayPoint.loading = false; })

      .addCase(batchDeleteWayPoints.pending, (state) => { state.WayPoints.loading = true; })
      .addCase(batchDeleteWayPoints.fulfilled, (state, action) => {
        state.WayPoints.loading = false;
        const { deletedIds } = action.payload;
        state.WayPoints.wayPointsList = state.WayPoints.wayPointsList.filter((item) => !deletedIds.includes(item.id));
      })
      .addCase(batchDeleteWayPoints.rejected, (state) => { state.WayPoints.loading = false; });
  },
});

export default WayPointSlice.reducer;
